import React, { useContext, useState, useCallback, useMemo, useRef } from "react";
import { FileDropZone, FileMetadata } from "../components/FileDropZone";
import { MediaUploadDialog } from "../components/MediaUploadDialog";
import { FilterType, ViewMode } from "./components/types";
import {
  formatFileSize,
  getFileCategory,
  getFileIcon,
  validateFileName,
} from "./components/utils";
import { MediaPreview } from "./components/MediaPreview";
import { PrimaryActionButton } from "@/util-components/page-layout";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { usePermissions } from "@/lib/hooks/use-permissions";
import { RefreshCcw, Upload } from "lucide-react";
import { toast } from "@/lib/toast";
import { annotateActiveSpan, recordDistributionMetric, withTelemetrySpan } from "@/lib/monitoring/telemetry";
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";
import {
  useMediaLibraryUpload,
  useMediaLibraryListQuery,
  useMediaLibraryDownload,
  useMediaLibraryGetCategories,
  useMediaLibraryReplace,
  useMediaLibraryBulkDelete,
} from "@/lib/api/requests/media-library.requests";
import { useBrowserUpload } from "../hooks/useBrowserUpload";
import { usePageState } from "@/lib/hooks/use-page-state";
import { EditMediaDialog } from "./components/EditMediaDialog";
import { DeleteMediaDialog } from "./components/DeleteMediaDialog";
import { MediaLibraryItemDto } from "@/lib/api/interfaces/media-library.interface";
import {
  DataView,
  DataViewToolbar,
  DataViewContent,
  DataViewLoadingState,
  DataViewEmptyState,
  DataViewLoadMore,
  DataViewGridCard,
  DataViewListCard,
  GenericItem,
  GenericItemActions,
} from "@/util-components/data-view";
import { Context } from "@/util-components/root/context";

const DEFAULT_PAGE_SIZE = 18;

/**
 * Media Library with modern, minimalistic UI.
 * Backend-driven search, sort and pagination (single source of truth).
 */
const MediaLibrary: React.FC = () => {
  const { user } = useContext(Context);
  const { state, setViewMode, setSettings } = usePageState(
    "post-generator-media-library",
    {
      viewMode: "grid",
      settings: {
        selectedCategory: "all",
      },
    },
  );

  const acceptedFileTypes = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/avif",
    "image/heic",
    "image/heif",
    "image/svg+xml",
  ];

  // Search & filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType] = useState<FilterType>("all");

  // View mode from State
  const viewMode = state.viewMode || "grid";

  // Upload dialog state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<FileMetadata[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [itemToPreview, setItemToPreview] =
    useState<MediaLibraryItemDto | null>(null);

  // Rename dialog state
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [itemToRename, setItemToRename] = useState<MediaLibraryItemDto | null>(
    null,
  );

  // Delete dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<MediaLibraryItemDto | null>(
    null,
  );
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Pagination and aggregation
  const [currentPage, setCurrentPage] = useState(1);
  const [allMedia, setAllMedia] = useState<MediaLibraryItemDto[]>([]);
  const [lastFilterKey, setLastFilterKey] = useState<string>("");

  // Category filter state
  const selectedCategory = state.settings?.selectedCategory || "all";

  // Permissions
  const { can } = usePermissions();
  const canManageEntities = can(Permission.MANAGE_ENTITIES);
  const plan = user?.subscriptionType || "noplan";

  const { mutate: bulkDeleteMutation, isPending: isBulkDeleting } =
    useMediaLibraryBulkDelete();

  /**
   * Key that represents the current filter/search state.
   * When it changes, pagination is reset and data re-aggregated.
   */
  const currentFilterKey = useMemo(
    () => `${searchQuery.trim()}|${filterType}|${selectedCategory}`,
    [searchQuery, filterType, selectedCategory],
  );

  // Fetch categories for dropdown
  const categoriesQuery = useMediaLibraryGetCategories({ enabled: true });
  const categories = useMemo(() => {
    const apiCategories = categoriesQuery.data?.categories || [];
    return ["all", "Unkategorisiert", ...apiCategories];
  }, [categoriesQuery.data]);

  // Backend pagination + search + sorting (single source of truth)
  const {
    data: mediaResponse,
    isLoading: isLoadingMedia,
    error: mediaError,
    refetch: refetchMedia,
  } = useMediaLibraryListQuery(
    {
      page: currentPage,
      limit: DEFAULT_PAGE_SIZE,
    },
    {
      textSearch: searchQuery.trim() || undefined,
      categories:
        selectedCategory === "all"
          ? undefined
          : selectedCategory === "Unkategorisiert"
            ? []
            : [selectedCategory],
      sortBy: "updated",
      direction: "desc",
    },
  );

  // Reset pagination when search/filter changes
  React.useEffect(() => {
    if (currentFilterKey !== lastFilterKey) {
      setCurrentPage(1);
      setAllMedia([]);
      setLastFilterKey(currentFilterKey);
    }
  }, [currentFilterKey, lastFilterKey]);

  // Append page results to aggregated list
  React.useEffect(() => {
    if (mediaResponse?.docs) {
      if (currentPage === 1) {
        setAllMedia(mediaResponse.docs);
      } else {
        setAllMedia((prev) => {
          const newDocs = mediaResponse.docs || [];
          const existingIds = new Set(prev.map((a) => a.mediaId));
          const filteredNewDocs = newDocs.filter(
            (d: any) => !existingIds.has(d.mediaId),
          );
          // For rename updates, we might want to update existing items, but since rename triggers a full refetch on page 1 usually (or we just reset to page 1),
          // Actually, if we just want to avoid duplicates:
          return [...prev, ...filteredNewDocs];
        });
      }
    }
  }, [mediaResponse?.docs, currentPage]);

  const summarizeFiles = useCallback((files: File[]) => {
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    const maxBytes = files.reduce((max, file) => Math.max(max, file.size), 0);
    const mimeTypes = Array.from(new Set(files.map((file) => file.type || "unknown")));

    return {
      totalBytes,
      maxBytes,
      mimeTypes,
      count: files.length,
    };
  }, []);

  /**
   * Upload mutation
   */
  const uploadMutation = useMediaLibraryUpload({
    onSuccess: (data) => {
      if (data && data.displayName && data.mediaId) {
        toast.success("Datei erfolgreich hochgeladen", {
          description: `${data.displayName} wurde zur Medienbibliothek hinzugefügt`,
        });
        setCurrentPage(1);
        refetchMedia();
      } else {
        console.error("Upload succeeded but returned invalid data:", data);
        toast.error("Upload fehlgeschlagen", {
          description:
            "Ungültige Antwort vom Server - Datei möglicherweise nicht gespeichert",
        });
      }
    },
    onError: (error) => {
      toast.error("Upload fehlgeschlagen", {
        description:
          (error as Error).message || "Ein unbekannter Fehler ist aufgetreten",
        error,
      });
    },
  });

  /**
   * Download mutation
   */
  const downloadMutation = useMediaLibraryDownload({
    onSuccess: () => {
      toast.success("Download gestartet", {
        description: "Die Datei wird heruntergeladen",
      });
    },
    onError: (error) => {
      toast.error("Download fehlgeschlagen", {
        description:
          (error as Error).message ||
          "Die Datei konnte nicht heruntergeladen werden",
        error,
      });
    },
  });

  /**
   * Replace mutation
   */
  const replaceMutation = useMediaLibraryReplace({
    onSuccess: (data) => {
      toast.success("Bild erfolgreich ersetzt", {
        description: `${data?.displayName ?? "Datei"} wurde aktualisiert`,
      });
      setCurrentPage(1);
      refetchMedia();
    },
    onError: (error) => {
      toast.error("Ersetzen fehlgeschlagen", {
        description:
          (error as Error).message || "Die Datei konnte nicht ersetzt werden",
        error,
      });
    },
  });

  // Keep track of which media item should be replaced (ref to avoid timing issues)
  const replaceTargetIdRef = useRef<string | null>(null);

  /**
   * Browser upload hook for replacing a single image
   */
  const { openFileDialog: openReplaceFileDialog } = useBrowserUpload({
    acceptedFileTypes,
    maxFiles: 1,
    maxFileSize: 5 * 1024 * 1024, // 5MB
    onFilesReady: (files: FileMetadata[]) => {
      const file = files[0]?.file;
      const targetId = replaceTargetIdRef.current;

      if (!targetId) {
        toast.error("Kein Bild zum Ersetzen ausgewählt");
        return;
      }
      if (!file) {
        toast.error("Keine gültige Datei ausgewählt");
        return;
      }

      replaceMutation.mutate({ id: targetId, file });
    },
    onError: (error) => {
      toast.error("Dateiauswahl fehlgeschlagen", {
        description: error,
      });
    },
  });

  /**
   * Convert MediaLibraryItemDto into a GenericItem for the list view
   */
  const convertToGenericItem = useCallback(
    (item: MediaLibraryItemDto): GenericItem => {
      const isSvg = item.mimeType.includes("svg");
      return {
        id: item.mediaId,
        title: item.displayName,
        previewContent: (
          <img
            src={isSvg ? item.url : item.thumbnailUrl || item.url}
            alt={item.displayName}
            className={`w-full h-full ${isSvg ? "object-contain p-4" : "object-cover"}`}
            loading="lazy"
          />
        ),
        metadata: {
          categories: item.categories || [],
          badgeText: formatFileSize(item.size),
          date: new Date(item.uploadedAt).toLocaleDateString("de-DE"),
        },
      };
    },
    [],
  );

  /**
   * Media items visible after applying MIME-type filter.
   * Text search is already handled by backend.
   */
  const visibleMedia = useMemo(
    () =>
      allMedia.filter((item) => {
        const matchesFilter =
          filterType === "all" || getFileCategory(item.mimeType) === filterType;
        return matchesFilter;
      }),
    [allMedia, filterType],
  );
  const hasFilters = searchQuery.trim().length > 0 || filterType !== "all";

  /**
   * Generic items for DynamicListView
   */
  const genericItems = useMemo(() => {
    return visibleMedia.map(convertToGenericItem);
  }, [visibleMedia, convertToGenericItem]);

  // Paging helpers
  const hasMoreItems = mediaResponse?.hasNextPage ?? false;

  // Prevent empty state flash by checking if we have data in query but not yet in state
  const isSyncing =
    (mediaResponse?.docs?.length ?? 0) > 0 && allMedia.length === 0;
  const isInitialLoading = (isLoadingMedia || isSyncing) && currentPage === 1;
  const isLoadingMore = isLoadingMedia && currentPage > 1;

  const handleLoadMore = useCallback(() => {
    if (hasMoreItems && !isLoadingMore) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasMoreItems, isLoadingMore]);

  /**
   * Handle files coming from browser file dialog (for upload dialog flow)
   */
  const handleFilesReady = useCallback((files: FileMetadata[]) => {
    setPendingFiles(files);
    setShowUploadDialog(true);
  }, []);

  /**
   * Browser upload hook (file dialog)
   */
  const { openFileDialog } = useBrowserUpload({
    acceptedFileTypes,
    maxFiles: 10,
    maxFileSize: 5 * 1024 * 1024, // 5MB
    onFilesReady: handleFilesReady,
    onError: (error) => {
      toast.error("Dateiauswahl fehlgeschlagen", {
        description: error,
      });
    },
  });

  /**
   * Open browser file dialog for upload
   */
  const handleUpload = useCallback(() => {
    openFileDialog();
  }, [openFileDialog]);

  /**
   * Handle drag-and-drop uploads (direct upload, no rename dialog)
   */
  const handleFileDrop = useCallback(
    async (files: File[]) => {
      const imageFiles = files.filter((file) => {
        if (!file.type.startsWith("image/")) return false;
        return true;
      });

      if (imageFiles.length === 0) {
        toast.error("Keine gültigen Bilddateien gefunden", {
          description:
            "Nur JPG, PNG, WEBP, SVG, HEIC und HEIF Dateien sind erlaubt",
        });
        return;
      }

      if (imageFiles.length !== files.length) {
        toast.warning("Einige Dateien wurden übersprungen", {
          description: "Nur Bilddateien sind erlaubt",
        });
      }

      const oversizedFiles = imageFiles.filter(
        (file) => file.size > 5 * 1024 * 1024,
      );
      if (oversizedFiles.length > 0) {
        toast.error("Dateien zu groß", {
          description: `${oversizedFiles.length} Datei(en) überschreiten das 5MB Limit`,
        });
        return;
      }

      try {
        const summary = summarizeFiles(imageFiles);
        let uploadSucceeded = false;

        await withTelemetrySpan(
          "media.upload.drag_drop",
          "feature.media.upload",
          {
            "media.upload.source": "drag_drop",
            "media.upload.file_count": summary.count,
            "media.upload.mime_types": summary.mimeTypes,
          },
          async () => {
            annotateActiveSpan(
              {},
              {
                "media.upload.total_size": { value: summary.totalBytes, unit: "byte" },
                "media.upload.max_file_size": { value: summary.maxBytes, unit: "byte" },
              },
            );

            for (const file of imageFiles) {
              await uploadMutation.mutateAsync({ file });
            }

            toast.success("Alle Dateien hochgeladen", {
              description: `${imageFiles.length} Bilddatei(en) erfolgreich hinzugefügt`,
            });
            uploadSucceeded = true;
          },
          {
            metricAttributes: {
              plan,
              source: "drag_drop",
            },
          },
        );

        recordDistributionMetric(
          "media.upload.files_per_batch",
          summary.count,
          "none",
          {
            plan,
            source: "drag_drop",
            success: uploadSucceeded,
          },
        );
      } catch (error) {
        console.error("Upload failed:", error);
        const summary = summarizeFiles(imageFiles);
        recordDistributionMetric(
          "media.upload.files_per_batch",
          summary.count,
          "none",
          {
            plan,
            source: "drag_drop",
            success: false,
          },
        );
        // Detailed error handling is handled inside the mutation's onError callback
      }
    },
    [plan, summarizeFiles, uploadMutation],
  );

  /**
   * Trigger a file download
   */
  const handleDownload = useCallback(
    async (mediaId: string) => {
      try {
        await downloadMutation.mutateAsync(mediaId);
      } catch (error) {
        console.error("Download failed:", error);
      }
    },
    [downloadMutation],
  );

  /**
   * Open rename dialog for a given media item (must be visible)
   */
  const handleRename = useCallback(
    (mediaId: string) => {
      const mediaItem = visibleMedia.find((item) => item.mediaId === mediaId);
      if (mediaItem) {
        setItemToRename(mediaItem);
        setShowRenameDialog(true);
      }
    },
    [visibleMedia],
  );

  const handleRenameSuccess = useCallback(() => {
    setShowRenameDialog(false);
    setItemToRename(null);
    setCurrentPage(1);
    refetchMedia();
  }, [refetchMedia]);

  /**
   * Open delete dialog for a given media item (must be visible)
   */
  const handleDelete = useCallback(
    (mediaId: string) => {
      const mediaItem = visibleMedia.find((item) => item.mediaId === mediaId);
      if (mediaItem) {
        setItemToDelete(mediaItem);
        setIsBulkDelete(false);
        setShowDeleteDialog(true);
      }
    },
    [visibleMedia],
  );

  const handleSelectionDelete = useCallback(() => {
    if (selectedIds.length > 0) {
      setItemToDelete(null);
      setIsBulkDelete(true);
      setShowDeleteDialog(true);
    }
  }, [selectedIds]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    if (selectedIds.length > 0) {
      bulkDeleteMutation(
        { ids: selectedIds },
        {
          onSuccess: async () => {
            toast.success(`${selectedIds.length} Medien erfolgreich gelöscht`);
            setAllMedia((prev) =>
              prev.filter((m) => !selectedIds.includes(m.mediaId)),
            );
            setSelectedIds([]);
            setShowDeleteDialog(false);
            setIsBulkDelete(false);
          },
          onError: (error) => {
            toast.error("Medien konnten nicht gelöscht werden", { error });
          },
        },
      );
    }
  }, [selectedIds, bulkDeleteMutation]);

  const handleDeleteSuccess = useCallback(async () => {
    setShowDeleteDialog(false);
    setAllMedia((prev) =>
      prev.filter((m) => m.mediaId !== itemToDelete?.mediaId),
    );
    setItemToDelete(null);
  }, [itemToDelete]);

  /**
   * View mode change → sync into State
   */
  const handleViewModeChange = useCallback(
    (newViewMode: ViewMode) => {
      setViewMode(newViewMode);
    },
    [setViewMode],
  );

  /**
   * Open preview for a given media item (must be visible)
   */
  const handlePreview = useCallback(
    (mediaId: string) => {
      const mediaItem = visibleMedia.find((item) => item.mediaId === mediaId);
      if (mediaItem) {
        setItemToPreview(mediaItem);
        setShowPreview(true);
      }
    },
    [visibleMedia],
  );

  const handlePreviewClose = useCallback(() => {
    setShowPreview(false);
    setItemToPreview(null);
  }, []);

  /**
   * Update file name in upload dialog (client-side validation)
   */
  const handleFileNameChange = useCallback(
    (fileId: string, newName: string) => {
      const validationError = validateFileName(newName);

      setPendingFiles((prev) =>
        prev.map((file) =>
          file.id === fileId
            ? {
                ...file,
                displayName: newName,
                error: validationError || undefined,
              }
            : file,
        ),
      );

      if (validationError) {
        toast.error(validationError);
      }
    },
    [],
  );

  /**
   * Remove a pending file from upload dialog queue
   */
  const handleFileRemove = useCallback(
    (fileId: string) => {
      const fileToRemove = pendingFiles.find((file) => file.id === fileId);
      setPendingFiles((prev) => prev.filter((file) => file.id !== fileId));

      if (fileToRemove) {
        toast.success(`${fileToRemove.displayName} entfernt`);
      }
    },
    [pendingFiles],
  );

  /**
   * Confirm upload for all pending files in dialog
   */
  const handleUploadConfirm = useCallback(async () => {
    if (pendingFiles.length === 0) {
      toast.error("Keine Dateien zum Upload ausgewählt");
      return;
    }

    setIsProcessing(true);

    try {
      const filesWithErrors = pendingFiles.filter((file) => file.error);
      if (filesWithErrors.length > 0) {
        toast.error(
          `${filesWithErrors.length} Datei(en) haben Fehler. Bitte korrigieren Sie diese zuerst.`,
        );
        setIsProcessing(false);
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      const summary = summarizeFiles(pendingFiles.map((fileMetadata) => fileMetadata.file));
      let uploadSucceeded = false;

      await withTelemetrySpan(
        "media.upload.dialog_confirm",
        "feature.media.upload",
        {
          "media.upload.source": "dialog_confirm",
          "media.upload.file_count": summary.count,
          "media.upload.mime_types": summary.mimeTypes,
        },
        async () => {
          annotateActiveSpan(
            {},
            {
              "media.upload.total_size": { value: summary.totalBytes, unit: "byte" },
              "media.upload.max_file_size": { value: summary.maxBytes, unit: "byte" },
            },
          );

          for (const fileMetadata of pendingFiles) {
            try {
              let fileToUpload: File;

              // Rebuild file if name was changed
              if (fileMetadata.displayName !== fileMetadata.file.name) {
                const arrayBuffer = await fileMetadata.file.arrayBuffer();
                const extension = fileMetadata.file.name?.split(".").pop() || "jpg";
                const newName = fileMetadata.displayName.includes(".")
                  ? fileMetadata.displayName
                  : `${fileMetadata.displayName}.${extension}`;

                fileToUpload = new File([arrayBuffer], newName, {
                  type: fileMetadata.file.type || "image/jpeg",
                  lastModified: fileMetadata.file.lastModified || Date.now(),
                });
              } else {
                fileToUpload = fileMetadata.file;
              }

              await uploadMutation.mutateAsync({ file: fileToUpload });
              successCount++;
            } catch (error) {
              console.error(
                "Error uploading file:",
                fileMetadata.displayName,
                error,
              );
              errorCount++;
            }
          }

          uploadSucceeded = successCount > 0 && errorCount === 0;
        },
        {
          metricAttributes: {
            plan,
            source: "dialog_confirm",
          },
        },
      );

      recordDistributionMetric(
        "media.upload.files_per_batch",
        summary.count,
        "none",
        {
          plan,
          source: "dialog_confirm",
          success: uploadSucceeded,
        },
      );

      if (successCount > 0 && errorCount === 0) {
        toast.success(`${successCount} Bild(er) erfolgreich hochgeladen`);
      } else if (successCount > 0 && errorCount > 0) {
        toast.warning(
          `${successCount} Bild(er) hochgeladen, ${errorCount} fehlgeschlagen`,
        );
      } else {
        toast.error("Alle Uploads fehlgeschlagen");
      }

      setShowUploadDialog(false);
      setPendingFiles([]);
    } catch (error) {
      console.error("Upload process error:", error);
      recordDistributionMetric(
        "media.upload.files_per_batch",
        pendingFiles.length,
        "none",
        {
          plan,
          source: "dialog_confirm",
          success: false,
        },
      );
      toast.error("Upload-Prozess fehlgeschlagen", { error });
    } finally {
      setIsProcessing(false);
    }
  }, [pendingFiles, plan, summarizeFiles, uploadMutation]);

  /**
   * Cancel pending uploads and close dialog
   */
  const handleUploadCancel = useCallback(() => {
    const fileCount = pendingFiles.length;
    setShowUploadDialog(false);
    setPendingFiles([]);
    setIsProcessing(false);

    if (fileCount > 0) {
      toast.info(`Upload von ${fileCount} Datei(en) abgebrochen`);
    }
  }, [pendingFiles]);

  /**
   * Generic item actions used by DynamicListView.
   * All actions operate on the currently visible media list.
   */
  const genericActions: GenericItemActions = {
    onItemClick: (id: string) => {
      handlePreview(id);
    },
    onPreview: (id: string) => {
      handlePreview(id);
    },
    onDownload: (id: string) => {
      handleDownload(id);
    },
    onEdit: (id: string) => {
      handleRename(id);
    },
    onDelete: (id: string) => {
      handleDelete(id);
    },
    // Add custom "Bild ersetzen" action
    customActions: [
      {
        label: "Bild ersetzen",
        icon: <RefreshCcw className="h-4 w-4" />,
        onClick: (id: string) => {
          replaceTargetIdRef.current = id;
          openReplaceFileDialog();
        },
        requiredPermission: Permission.MANAGE_ENTITIES,
        requiredPlan: PlanKey.BASIC,
      },
    ],
  };

  return (
    <DataView
      viewMode={viewMode}
      onViewModeChange={handleViewModeChange}
      isLoading={isInitialLoading}
      isEmpty={visibleMedia.length === 0}
      selectedIds={selectedIds}
      onMultiSelect={setSelectedIds}
      onSelectionDelete={handleSelectionDelete}
    >
      <DataViewToolbar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Medien suchen..."
        filterParams={categories.map((category) => ({
          key: category,
          label: category === "all" ? "Alle Kategorien" : category,
          value: category,
        }))}
        selectedFilter={selectedCategory}
        onFilterChange={(val) => setSettings({ selectedCategory: val })}
        filterLabel="Kategorie"
        infoText={`${mediaResponse?.totalDocs ?? visibleMedia.length} Medien`}
        selectedIds={selectedIds}
        onSelectionDelete={handleSelectionDelete}
        leading={
          <PermissionFeatureWrapper
            requiredPermission={Permission.MANAGE_ENTITIES}
            requiredPlan={PlanKey.BASIC}
          >
            <PrimaryActionButton
              onClick={handleUpload}
              label="Upload"
              icon={Upload}
            />
          </PermissionFeatureWrapper>
        }
      />

      <FileDropZone
        acceptedFileTypes={acceptedFileTypes}
        onFileDrop={handleFileDrop}
        onFilesReady={handleFilesReady}
        overlayText="Bilder hier ablegen zum Hochladen"
        className="flex-1 overflow-auto p-4"
        disabled={!canManageEntities}
      >
        {mediaError ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-red-500 mb-2">
                Fehler beim Laden der Medienbibliothek
              </p>
              <button
                onClick={() => refetchMedia()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Erneut versuchen
              </button>
            </div>
          </div>
        ) : (
          <>
            <DataViewContent
              items={genericItems}
              renderGridItem={(item) => (
                <DataViewGridCard item={item} actions={genericActions} />
              )}
              renderListItem={(item) => (
                <DataViewListCard item={item} actions={genericActions} />
              )}
              loadingIndicator={<DataViewLoadingState />}
              emptyState={
                <DataViewEmptyState
                  icon={<Upload className="h-8 w-8 text-gray-400" />}
                  title="Keine Medien gefunden"
                  description={
                    hasFilters
                      ? "Versuche andere Suchbegriffe oder Filter."
                      : "Lade deine ersten Medien hoch und beginne mit der Gestaltung."
                  }
                  action={
                    <PermissionFeatureWrapper
                      requiredPermission={Permission.MANAGE_ENTITIES}
                      requiredPlan={PlanKey.BASIC}
                    >
                      <PrimaryActionButton
                        onClick={handleUpload}
                        label="Medien hochladen"
                        icon={Upload}
                      />
                    </PermissionFeatureWrapper>
                  }
                />
              }
            />
            <DataViewLoadMore
              onLoadMore={handleLoadMore}
              isLoadingMore={isLoadingMore}
              hasMore={hasMoreItems}
            />
          </>
        )}
      </FileDropZone>

      <MediaUploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        files={pendingFiles}
        isProcessing={isProcessing}
        onFileNameChange={handleFileNameChange}
        onFileRemove={handleFileRemove}
        onUploadConfirm={handleUploadConfirm}
        onUploadCancel={handleUploadCancel}
        getFileIcon={getFileIcon}
        formatFileSize={formatFileSize}
      />

      {itemToRename && (
        <EditMediaDialog
          open={showRenameDialog}
          onOpenChange={setShowRenameDialog}
          mediaItem={itemToRename}
          onSuccess={handleRenameSuccess}
        />
      )}

      {itemToDelete && !isBulkDelete && (
        <DeleteMediaDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          mediaItem={itemToDelete}
          onSuccess={handleDeleteSuccess}
        />
      )}

      {isBulkDelete && (
        <StandardDeleteDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          title={`${selectedIds.length} Medien löschen`}
          description="Möchten Sie diese Medien wirklich unwiderruflich löschen?"
          itemsList={selectedIds.map((id) => {
            const m = allMedia.find((m) => m.mediaId === id);
            return {
              title: m ? m.displayName : id,
            };
          })}
          onDelete={handleBulkDeleteConfirm}
          isLoading={isBulkDeleting}
        />
      )}

      {itemToPreview && (
        <MediaPreview
          open={showPreview}
          onOpenChange={handlePreviewClose}
          item={itemToPreview}
          onDelete={handleDelete}
          onDownload={handleDownload}
          onEdit={handleRename}
        />
      )}
    </DataView>
  );
};

export default MediaLibrary;
